import { EditCategoryWidget } from '@modules/common/components/EditCategoryWidget';
import { InputTags } from '@modules/common/components/InputTags';
import { TrackedKeywordFormPhrasesSVGIcon } from '@modules/common/components/SVGIcons';
import { TRACKED_KEYWORD } from '@modules/common/constants';
import { Category } from '@modules/common/types';
import { TrackedKeywordPhrasesControlLabel } from '@modules/taxonomy/components/TrackedKeywords/TrackedKeywordPhrasesControlLabel';
import {
  EXCLUDED_PHRASES_TOOLTIP_CONTENT,
  INCLUDED_PHRASES_TOOLTIP_CONTENT,
} from '@modules/taxonomy/constants';
import { AddRounded, RemoveRounded } from '@mui/icons-material';
import { Stack, TextField, Typography } from '@mui/material';
import { UUID } from 'v2/core/entities/uuid';

interface AddNewTrackedKeywordFormProps {
  feedbackID: UUID;
  phraseList: string[];
  setPhraseList: React.Dispatch<React.SetStateAction<string[]>>;
  excludedPhraseList: string[];
  setExcludedPhraseList: React.Dispatch<React.SetStateAction<string[]>>;
  trackedKeywordName: string;
  setTrackedKeywordName: (trackedKeywordName: string) => void;
  selectedCategory: Category | undefined;
  setSelectedCategory: (selectedCategory: Category) => void;
}

export const AddNewTrackedKeywordForm = ({
  feedbackID,
  phraseList,
  setPhraseList,
  excludedPhraseList,
  setExcludedPhraseList,
  trackedKeywordName,
  setTrackedKeywordName,
  selectedCategory,
  setSelectedCategory,
}: AddNewTrackedKeywordFormProps) => {
  return (
    <Stack spacing={4}>
      <Stack spacing={1}>
        <Stack component="label" htmlFor="trackedKeywordName" spacing={0.5}>
          {TRACKED_KEYWORD} Name
        </Stack>
        <TextField
          type="text"
          name="trackedKeywordName"
          required
          placeholder={`${TRACKED_KEYWORD} name`}
          fullWidth
          variant="outlined"
          autoFocus
          value={trackedKeywordName}
          onChange={({ target: { value } }) =>
            setTrackedKeywordName(value.trim())
          }
        />
      </Stack>
      <Stack spacing={1}>
        <Typography>Category to tag this record with</Typography>
        <EditCategoryWidget
          selectedCategory={selectedCategory}
          updateSelectedCategory={setSelectedCategory}
        />
      </Stack>
      <Stack pb={4}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <TrackedKeywordFormPhrasesSVGIcon />
          <Typography color="text.secondary">Phrases</Typography>
        </Stack>
        <Stack pt={2} spacing={3}>
          <TrackedKeywordPhrasesControlLabel
            label={
              <Stack direction="row" spacing={1}>
                <AddRounded sx={{ opacity: 0.7 }} />
                <Typography color="text.secondary">Included Phrases</Typography>
              </Stack>
            }
            tooltipContent={INCLUDED_PHRASES_TOOLTIP_CONTENT}
            phrasesCount={<Typography>({phraseList.length})</Typography>}
            control={
              <InputTags
                size="small"
                name="phrases"
                tags={phraseList}
                placeholder="Enter comma-separated values"
                onChange={(_, tags) => setPhraseList(tags)}
                sx={{
                  maxHeight: 200,
                  overflow: 'auto',
                }}
              />
            }
          />
          <TrackedKeywordPhrasesControlLabel
            label={
              <Stack direction="row" spacing={1}>
                <RemoveRounded sx={{ opacity: 0.7 }} />
                <Typography color="text.secondary">Excluded Phrases</Typography>
              </Stack>
            }
            phrasesCount={
              <Typography>({excludedPhraseList.length})</Typography>
            }
            tooltipContent={EXCLUDED_PHRASES_TOOLTIP_CONTENT}
            control={
              <InputTags
                size="small"
                name="phrases"
                tags={excludedPhraseList}
                placeholder="Enter comma-separated values"
                onChange={(_, tags) => setExcludedPhraseList(tags)}
                sx={{
                  maxHeight: 200,
                  overflow: 'auto',
                }}
              />
            }
          />
        </Stack>
      </Stack>
    </Stack>
  );
};
